import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { usePurchaseOrders } from "../../contexts/albina/PurchaseOrdersContext";
import STORE_NAMES from "../../utils/STORE_NAMES";

// ===== Animations & Styles =====
const floatIn = keyframes`
  0% { opacity: 0; transform: translateY(16px) scale(0.96); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "2rem",
  fontWeight: 700,
  background: "linear-gradient(90deg, #06b6d4, #6366f1, #4f46e5)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  animation: `${shimmer} 4s linear infinite`,
}));

// === GlassCard for Charts & Categories ===
const GlassCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.7), rgba(240,249,255,0.8))",
  boxShadow: "0 12px 35px rgba(0,0,0,0.15)",
  backdropFilter: "blur(8px)",
  transition: "all 0.4s ease",
  animation: `${floatIn} 0.6s ease both`,
  padding: "1rem",
  "&:hover": {
    transform: "translateY(-8px) scale(1.02)",
    boxShadow: "0 18px 45px rgba(79,70,229,0.4)",
  },
}));

// === Animated KPI Card ===
const AnimatedKPICard = styled(Card)(({ gradient }) => ({
  borderRadius: 16,
  color: "#fff",
  textAlign: "center",
  padding: "2rem",
  background: gradient,
  backgroundSize: "300% 300%",
  animation: `${gradientShift} 8s ease infinite, ${floatIn} 0.6s ease both`,
  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
  "&:hover": {
    transform: "translateY(-8px) scale(1.02)",
    boxShadow: "0 16px 40px rgba(0,0,0,0.35)",
  },
}));

const COLORS = ["#06b6d4", "#6366f1", "#4f46e5", "#0ea5e9", "#3b82f6"];

const KPICard = ({ title, value, subtitle, gradient }) => (
  <AnimatedKPICard gradient={gradient}>
    <CardContent>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700 }} gutterBottom>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </AnimatedKPICard>
);

const Home = () => {
  const {
    purchaseOrders,
    purchaseOrders1,
    purchaseOrders2,
    purchaseOrders3,
    purchaseOrders4,
    purchaseOrders5,
    purchaseOrders6,
    suppliers,
    goods,
    categories,
    total,
    total1,
    total2,
    total3,
    total4,
    total5,
    total6,
    
    loading,
  } = usePurchaseOrders();

  const endpoint0 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_0;
  const endpoint1 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_1;
  const endpoint2 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_2;
  const endpoint3 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_3;
  const endpoint4 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_4;
  const endpoint5 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_5;
  const endpoint6 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_6;
  
  const getStoreFromUrl = (url) => {
      try {
        const params = new URL(url).searchParams;
        return params.get("0_store");
      } catch (err) {
        console.error("Invalid URL:", err);
        return null;
      }
    };
  
    const storeId0 = getStoreFromUrl(endpoint0);
    const storeId1 = getStoreFromUrl(endpoint1);
    const storeId2 = getStoreFromUrl(endpoint2);
    const storeId3 = getStoreFromUrl(endpoint3);
    const storeId4 = getStoreFromUrl(endpoint4);
    const storeId5 = getStoreFromUrl(endpoint5);
    const storeId6 = getStoreFromUrl(endpoint6);
    

  
    const storeName0 = STORE_NAMES[storeId0] || "Unknown Store";
    const storeName1 = STORE_NAMES[storeId1] || "Unknown Store";
    const storeName2 = STORE_NAMES[storeId2] || "Unknown Store";
    const storeName3 = STORE_NAMES[storeId3] || "Unknown Store";
    const storeName4 = STORE_NAMES[storeId4] || "Unknown Store";
    const storeName5 = STORE_NAMES[storeId5] || "Unknown Store";
    const storeName6 = STORE_NAMES[storeId6] || "Unknown Store";
    
  


  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // KPIs
  const totalOrders =
    purchaseOrders.length + purchaseOrders1.length + purchaseOrders2.length + purchaseOrders3.length + purchaseOrders4.length + purchaseOrders5.length + purchaseOrders6.length;
  const totalValue = total + total1 + total2 + total3 + total4 + total5 + total6;
  const totalSuppliers = suppliers.length;
  const totalGoods = goods.length;
  const totalCategories = categories.length;

  // Chart data
  const ordersPerStoreData = [
    { name: storeName0, orders: purchaseOrders.length, value: total },
    { name: storeName1, orders: purchaseOrders1.length, value: total1 },
    { name: storeName2, orders: purchaseOrders2.length, value: total2 },
    { name: storeName3, orders: purchaseOrders3.length, value: total3 },
    { name: storeName4, orders: purchaseOrders4.length, value: total4 },
    { name: storeName5, orders: purchaseOrders5.length, value: total5 },
    { name: storeName6, orders: purchaseOrders6.length, value: total6 },
    
  ];

  const supplierDistribution = suppliers
    .slice(0, 5)
    .map((supplier, index) => ({
      name: supplier.name || `Supplier ${index + 1}`,
      value: Math.floor(Math.random() * 100) + 10,
    }));

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 3,
        background: "linear-gradient(180deg,#f8fafc,#eef2ff)",
        minHeight: "100vh",
      }}
    >
      <Title gutterBottom>Dashboard Overview</Title>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 4 }} >
        {[
          {
            title: "Total Orders",
            value: totalOrders,
            subtitle: "Across all stores",
            gradient: "linear-gradient(270deg, #06b6d4, #3b82f6, #4f46e5)",
          },
          {
            title: "Total Value",
            value: `AED ${totalValue.toFixed(2)}`,
            subtitle: "Combined value",
            gradient: "linear-gradient(270deg, #ff416c, #ff4b2b, #ef4444)",
          },
          {
            title: "Suppliers",
            value: totalSuppliers,
            subtitle: "Active suppliers",
            gradient: "linear-gradient(270deg, #10b981, #3b82f6, #0ea5e9)",
          },
          {
            title: "Goods",
            value: totalGoods,
            subtitle: "Available goods",
            gradient: "linear-gradient(270deg, #f59e0b, #ef4444, #6366f1)",
          },
        ].map((kpi, index) => (
          <Grid item style={{width : '24%'}} key={index}>
            <KPICard {...kpi} />
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid
        container
        spacing={2}
        sx={{
          mb: 3,
          //justifyContent: "center",
          //flexWrap: "wrap",
        }}
      >
        {/* Orders per Store */}
        <Grid item style={{width : '49%'}}>
          <GlassCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Orders per Store
              </Typography>
              <ResponsiveContainer width="100%" height={500}>
                <BarChart data={ordersPerStoreData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 14, fill: "#374151" }} />
                  <YAxis tick={{ fontSize: 14, fill: "#374151" }} />
                  <Tooltip cursor={{ fill: "rgba(99,102,241,0.1)" }} />
                  <Legend />
                  <Bar dataKey="orders" radius={[10, 10, 0, 0]}>
                    {ordersPerStoreData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </GlassCard>
        </Grid>

        {/* Value per Store */}
        <Grid item style={{width : '49%'}} md={4}>
          <GlassCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Value per Store
              </Typography>
              <ResponsiveContainer width="100%" height={500}>
                <BarChart data={ordersPerStoreData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 14, fill: "#374151" }} />
                  <YAxis tick={{ fontSize: 14, fill: "#374151" }} />
                  <Tooltip
                    formatter={(value) => `AED ${value.toFixed(2)}`}
                    cursor={{ fill: "rgba(6,182,212,0.08)" }}
                  />
                  <Legend />
                  <Bar dataKey="value" radius={[10, 10, 0, 0]}>
                    {ordersPerStoreData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[(index + 2) % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </GlassCard>
        </Grid>

        {/* Supplier Distribution */}
        <Grid item style={{width:'49%'}} md={4}>
          <GlassCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Supplier Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={500}>
                <PieChart>
                  <Pie
                    data={supplierDistribution}
                    cx="50%"
                    cy="50%"
                    outerRadius={130}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {supplierDistribution.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </GlassCard>
        </Grid>

        {/* Categories - full width */}
        <Grid item xs={12} style={{width:'49%'}} >
          <GlassCard sx={{ textAlign: "center", py: 6 ,  }}>
            <Typography variant="h6" gutterBottom>
              Categories
            </Typography>
            <Typography variant="h2" color="primary" gutterBottom>
              {totalCategories}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Product categories available
            </Typography>
          </GlassCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Home;
